import type { BuilderError } from "./parse-save-errors";
import { iterTree } from "./tree";
import type { TIntakeDefinition, TIntakeTreeElement } from "@/types/intake";

/**
 * Client-side preflight for the draft definition. Mirrors a focused subset of
 * the backend schema validator so authors get inline + Save-disabled feedback
 * before the request lands. The backend remains the source of truth — this is
 * a UX accelerator, not the validation contract.
 *
 * Surfaces errors that match the most common 400 responses from
 * ``cognota.intake.schema.validate_definition``:
 *
 * English is the canonical locale and is the only one required; French (and
 * any other locale) is optional, matching the backend validator.
 *
 * - Page name: ``name.en`` non-empty.
 * - Value-bearing leaf (``text`` / ``textarea`` / ``email`` / etc.): ``label.en``
 *   non-empty.
 * - Choice element (``radio`` / ``checkboxes`` / ``select``): every option has a
 *   non-empty ``value`` + ``label.en``; options list non-empty.
 * - ``info_text`` / ``rich_text`` leaf: ``content.en`` non-empty.
 *
 * Intentionally skipped:
 * - ``system_field`` rows — locked + managed by the store.
 * - ``field_group.label`` — optional per backend schema (a group can be a pure
 *   visual cluster).
 * - ``columns`` shape — bounded by the editor; the column-count selector
 *   already constrains it to 2..4.
 *
 * Returns ``BuilderError[]`` shaped so callers can merge with the existing
 * post-save inline-error plumbing.
 */
export function validateDraft(definition: TIntakeDefinition | null): BuilderError[] {
  if (!definition) return [];
  const errors: BuilderError[] = [];

  definition.pages.forEach((page, pageIndex) => {
    const pagePath = `pages[${pageIndex}]`;

    if (!page.name?.en?.trim()) {
      errors.push({
        raw: `${pagePath}.name.en is required.`,
        pageIndex,
        field: "name.en",
      });
    }

    for (const { element } of iterTree(page)) {
      collectElementErrors(element, pageIndex, errors);
    }
  });

  return errors;
}

function collectElementErrors(
  element: TIntakeTreeElement,
  pageIndex: number,
  errors: BuilderError[],
): void {
  // System-field labels are author-editable (2026-06-11); EN stays the
  // canonical required locale. Other element config remains locked.
  if (element.kind === "system_field") {
    if (!element.label?.en?.trim()) {
      errors.push({
        raw: "Element English label is required.",
        pageIndex,
        elementId: element.id,
        field: "label.en",
      });
    }
    return;
  }

  // Containers themselves carry no required label/content; their children are
  // validated separately via the iterTree walk.
  if (element.kind === "field_group" || element.kind === "columns") return;
  if (element.kind === "divider") return;

  if (element.kind === "info_text") {
    if (!element.content?.en?.trim()) {
      errors.push({
        raw: "Info text English content is required.",
        pageIndex,
        elementId: element.id,
        field: "content.en",
      });
    }
    return;
  }

  if (element.kind === "rich_text") {
    // The factory seeds ``content.en = "<p></p>"``; a raw ``.trim()`` treats that
    // markup as non-empty, so a never-edited block would publish blank. Strip tags
    // first so tags-only content counts as empty.
    const text = element.content?.en?.replace(/<[^>]*>/g, "").trim();
    if (!text) {
      errors.push({
        raw: "Rich text English content is required.",
        pageIndex,
        elementId: element.id,
        field: "content.en",
      });
    }
    return;
  }

  // All remaining kinds are value-bearing leaves with a localized label.
  if (!element.label?.en?.trim()) {
    errors.push({
      raw: "Element English label is required.",
      pageIndex,
      elementId: element.id,
      field: "label.en",
    });
  }

  if (element.kind === "radio" || element.kind === "checkboxes" || element.kind === "select") {
    if (element.options.length === 0) {
      errors.push({
        raw: "Choice element must have at least one option.",
        pageIndex,
        elementId: element.id,
        field: "options",
      });
      return;
    }
    element.options.forEach((option, optionIndex) => {
      if (!option.value?.trim()) {
        errors.push({
          raw: `Option ${optionIndex + 1} value is required.`,
          pageIndex,
          elementId: element.id,
          optionIndex,
          field: `options[${optionIndex}].value`,
        });
      }
      if (!option.label?.en?.trim()) {
        errors.push({
          raw: `Option ${optionIndex + 1} English label is required.`,
          pageIndex,
          elementId: element.id,
          optionIndex,
          field: `options[${optionIndex}].label.en`,
        });
      }
    });
  }
}
